import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Check, X, Play, Trash2, GripVertical } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';

/**
 * Run a quick accept/reject simulation on an automaton.
 * Returns 'accepted' | 'rejected' | 'stuck' | 'no-initial' | 'undetermined'
 */
function simulateString(automaton, rawInput) {
  if (!automaton?.states?.length) return 'no-initial';
  const input = (rawInput === '\u03BB' || rawInput === '\u03B5') ? '' : rawInput;
  const initial = automaton.states.find(s => s.is_initial);
  if (!initial) return 'no-initial';

  if (automaton.type === 'PDA') return simulatePdaString(automaton, input, initial);
  if (automaton.type === 'TM') return simulateTmString(automaton, input, initial);

  // Epsilon closure for NFA
  const EPSILON = new Set(['\u03BB', '\u03B5', 'epsilon', '\u025B', '']);
  const epsClosure = (stateIds) => {
    const closed = new Set(stateIds);
    const stack = [...stateIds];
    while (stack.length > 0) {
      const sid = stack.pop();
      for (const t of automaton.transitions) {
        if (t.from_state === sid && t.symbols.some(s => EPSILON.has(s)) && !closed.has(t.to_state)) {
          closed.add(t.to_state);
          stack.push(t.to_state);
        }
      }
    }
    return [...closed];
  };

  let curStates = epsClosure([initial.id]);
  for (let i = 0; i < input.length; i++) {
    const symbol = input[i];
    const next = new Set();
    for (const sid of curStates) {
      for (const t of automaton.transitions) {
        if (t.from_state === sid && t.symbols.includes(symbol)) next.add(t.to_state);
      }
    }
    if (next.size === 0) return 'stuck';
    curStates = epsClosure([...next]);
  }
  return curStates.some(id => automaton.states.find(s => s.id === id)?.is_final)
    ? 'accepted' : 'rejected';
}

function simulateTmString(automaton, input, initial) {
  const blankSym = automaton.blank_symbol || '\u25A1';
  const stepLimit = automaton.step_limit || 10000;
  const tape = {};
  for (let i = 0; i < input.length; i++) tape[i] = input[i];

  let headPos = 0;
  let curStateId = initial.id;

  for (let step = 0; step < stepLimit; step++) {
    const curState = automaton.states.find(s => s.id === curStateId);
    if (curState?.is_final) return { status: 'accepted', tape: extractTapeString(tape, blankSym) };
    if (curState?.is_reject) return { status: 'rejected', tape: extractTapeString(tape, blankSym) };

    const readSym = tape[headPos] || blankSym;
    let matchRule = null;
    let nextStateId = null;
    for (const t of automaton.transitions) {
      if (t.from_state !== curStateId) continue;
      for (const r of (t.tm_rules || [])) {
        if ((r.read || blankSym) === readSym) { matchRule = r; nextStateId = t.to_state; break; }
      }
      if (matchRule) break;
    }
    if (!matchRule) return { status: 'stuck', tape: extractTapeString(tape, blankSym) };

    tape[headPos] = matchRule.write || blankSym;
    if (matchRule.direction === 'L') headPos--;
    else if (matchRule.direction === 'R') headPos++;
    curStateId = nextStateId;
  }
  return { status: 'loop', tape: extractTapeString(tape, blankSym) };
}

/** Extract non-blank tape contents as a string */
function extractTapeString(tape, blankSym) {
  const positions = Object.keys(tape).map(Number).sort((a, b) => a - b);
  if (positions.length === 0) return '';
  const min = positions[0];
  const max = positions[positions.length - 1];
  let result = '';
  for (let i = min; i <= max; i++) {
    result += tape[i] || blankSym;
  }
  // Trim leading/trailing blanks
  return result.replace(new RegExp(`^\\${blankSym}+|\\${blankSym}+$`, 'g'), '');
}

function simulatePdaString(automaton, input, initial) {
  const z0 = automaton.initial_stack_symbol || 'Z';
  const acceptMode = automaton.acceptance_mode || 'final_state';
  const MAX_CONFIGS = 1000;
  const LAMBDA = '\u03BB';
  const EPSILON = '\u03B5';
  const isEmpty = (s) => !s || s === LAMBDA || s === EPSILON;

  let configs = [{ stateId: initial.id, stack: [z0], inputPos: 0 }];
  let totalExplored = 0;

  const epsilonClose = (pos) => {
    let changed = true;
    let iter = 0;
    while (changed && iter < 50) {
      changed = false;
      iter++;
      const newConfigs = [];
      for (const cfg of configs) {
        if (cfg.inputPos !== pos) continue;
        for (const t of automaton.transitions) {
          if (t.from_state !== cfg.stateId) continue;
          for (const r of (t.pda_rules || [])) {
            if (!isEmpty(r.input)) continue;
            const stackTop = cfg.stack.length > 0 ? cfg.stack[cfg.stack.length - 1] : null;
            if (!isEmpty(r.pop) && stackTop !== r.pop) continue;
            const newStack = [...cfg.stack];
            if (!isEmpty(r.pop)) newStack.pop();
            if (!isEmpty(r.push)) {
              for (let k = r.push.length - 1; k >= 0; k--) {
                if (r.push[k] !== LAMBDA && r.push[k] !== EPSILON) newStack.push(r.push[k]);
              }
            }
            const key = `${t.to_state}|${newStack.join(',')}|${pos}`;
            if (!configs.some(c => `${c.stateId}|${c.stack.join(',')}|${c.inputPos}` === key) &&
                !newConfigs.some(c => `${c.stateId}|${c.stack.join(',')}|${c.inputPos}` === key)) {
              newConfigs.push({ stateId: t.to_state, stack: newStack, inputPos: pos });
              changed = true;
              totalExplored++;
              if (totalExplored > MAX_CONFIGS) return 'limit';
            }
          }
        }
      }
      configs = [...configs, ...newConfigs];
    }
    return 'ok';
  };

  // Initial epsilon closure
  if (epsilonClose(0) === 'limit') return 'undetermined';

  for (let i = 0; i < input.length; i++) {
    const symbol = input[i];
    const nextConfigs = [];
    for (const cfg of configs) {
      if (cfg.inputPos !== i) continue;
      for (const t of automaton.transitions) {
        if (t.from_state !== cfg.stateId) continue;
        for (const r of (t.pda_rules || [])) {
          if (r.input !== symbol) continue;
          const stackTop = cfg.stack.length > 0 ? cfg.stack[cfg.stack.length - 1] : null;
          if (!isEmpty(r.pop) && stackTop !== r.pop) continue;
          const newStack = [...cfg.stack];
          if (!isEmpty(r.pop)) newStack.pop();
          if (!isEmpty(r.push)) {
            for (let k = r.push.length - 1; k >= 0; k--) {
              if (r.push[k] !== LAMBDA && r.push[k] !== EPSILON) newStack.push(r.push[k]);
            }
          }
          nextConfigs.push({ stateId: t.to_state, stack: newStack, inputPos: i + 1 });
          totalExplored++;
          if (totalExplored > MAX_CONFIGS) return 'undetermined';
        }
      }
    }
    if (nextConfigs.length === 0) return 'stuck';
    configs = nextConfigs;

    // Epsilon closure at new position
    if (epsilonClose(i + 1) === 'limit') return 'undetermined';
  }

  const finalConfigs = configs.filter(c => c.inputPos === input.length);
  if (acceptMode === 'final_state') {
    return finalConfigs.some(c => automaton.states.find(s => s.id === c.stateId)?.is_final) ? 'accepted' : 'rejected';
  }
  return finalConfigs.some(c => c.stack.length === 0) ? 'accepted' : 'rejected';
}

// Confusion-matrix colors
const STATUS_STYLES = {
  tp: { color: 'text-green-600 dark:text-green-400', bg: 'bg-green-500/10', icon: Check, label: 'Correctly accepted' },
  fn: { color: 'text-orange-600 dark:text-orange-400', bg: 'bg-orange-500/10', icon: X, label: 'Should be accepted but rejected' },
  tn: { color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-500/10', icon: Check, label: 'Correctly rejected' },
  fp: { color: 'text-red-600 dark:text-red-400', bg: 'bg-red-500/10', icon: X, label: 'Should be rejected but accepted' },
};

export function TestingStrings({ currentAutomaton, updateCurrentAutomaton, onOpenSimulator, isReadOnly }) {
  const acceptStrings = currentAutomaton?.test_accept_strings || [];
  const rejectStrings = currentAutomaton?.test_reject_strings || [];

  const addString = useCallback((type, value) => {
    const key = type === 'accept' ? 'test_accept_strings' : 'test_reject_strings';
    const current = currentAutomaton?.[key] || [];
    if (!current.includes(value)) {
      updateCurrentAutomaton({ [key]: [...current, value] });
    }
  }, [currentAutomaton, updateCurrentAutomaton]);

  const removeString = useCallback((type, value) => {
    const key = type === 'accept' ? 'test_accept_strings' : 'test_reject_strings';
    const current = currentAutomaton?.[key] || [];
    updateCurrentAutomaton({ [key]: current.filter(s => s !== value) });
  }, [currentAutomaton, updateCurrentAutomaton]);

  const reorderStrings = useCallback((type, fromIdx, toIdx) => {
    const key = type === 'accept' ? 'test_accept_strings' : 'test_reject_strings';
    const current = [...(currentAutomaton?.[key] || [])];
    const [moved] = current.splice(fromIdx, 1);
    current.splice(toIdx, 0, moved);
    updateCurrentAutomaton({ [key]: current });
  }, [currentAutomaton, updateCurrentAutomaton]);

  // Memoize simulation results to avoid recomputation on every render
  const acceptResults = useMemo(() =>
    acceptStrings.map(s => ({ str: s, result: simulateString(currentAutomaton, s) })),
    [acceptStrings, currentAutomaton]
  );
  const rejectResults = useMemo(() =>
    rejectStrings.map(s => ({ str: s, result: simulateString(currentAutomaton, s) })),
    [rejectStrings, currentAutomaton]
  );

  return (
    <div className="flex h-full">
      <div className="flex-1 grid grid-cols-2 gap-0 divide-x divide-border min-w-0">
        <StringSection
          title="Accept Strings"
          hint="all should be green"
          type="accept"
          results={acceptResults}
          onAdd={addString}
          onRemove={removeString}
          onReorder={reorderStrings}
          onOpenSimulator={onOpenSimulator}
          getStatus={(result) => { const s = typeof result === 'object' ? result.status : result; return s === 'accepted' ? 'tp' : 'fn'; }}
          isTM={currentAutomaton?.type === 'TM'}
          testIdPrefix="accept"
          isReadOnly={isReadOnly}
        />
        <StringSection
          title="Reject Strings"
          hint="all should be blue"
          type="reject"
          results={rejectResults}
          onAdd={addString}
          onRemove={removeString}
          onReorder={reorderStrings}
          onOpenSimulator={onOpenSimulator}
          getStatus={(result) => { const s = typeof result === 'object' ? result.status : result; return (s === 'rejected' || s === 'stuck' || s === 'no-initial' || s === 'loop') ? 'tn' : 'fp'; }}
          isTM={currentAutomaton?.type === 'TM'}
          testIdPrefix="reject"
          isReadOnly={isReadOnly}
        />
      </div>
      <Legend />
    </div>
  );
}

function StringSection({ title, hint, type, results, onAdd, onRemove, onReorder, onOpenSimulator, getStatus, isTM, testIdPrefix, isReadOnly }) {
  const [input, setInput] = useState('');
  const inputRef = useRef(null);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);

  // Handle special character shortcuts: \l → λ, \e → ε
  const handleInputChange = (e) => {
    let val = e.target.value;
    val = val.replace(/\\l/g, '\u03BB').replace(/\\e/g, '\u03B5').replace(/\\_/g, '\u25A1');
    setInput(val);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && input.trim() !== '') {
      e.preventDefault();
      onAdd(type, input);
      setInput('');
    }
  };

  // Auto-focus input when section mounts
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleDragStart = (e, idx) => {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', idx.toString());
  };

  const handleDragOver = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (overIdx !== idx) setOverIdx(idx);
  };

  const handleDrop = (e, idx) => {
    e.preventDefault();
    if (dragIdx !== null && dragIdx !== idx) {
      onReorder(type, dragIdx, idx);
    }
    setDragIdx(null);
    setOverIdx(null);
  };

  const handleDragEnd = () => {
    setDragIdx(null);
    setOverIdx(null);
  };

  return (
    <div className="flex flex-col overflow-hidden" data-testid={`${testIdPrefix}-section`}>
      <div className="px-3 pt-2 pb-1 shrink-0">
        <div className="flex items-center gap-2 mb-1">
          <h4 className="text-xs font-semibold text-foreground">{title}</h4>
          <span className="text-[10px] text-muted-foreground italic">{hint}</span>
        </div>
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={isReadOnly ? 'Read-only' : 'type string, press Enter'}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          disabled={isReadOnly}
          className="w-full h-7 px-2 text-sm font-mono bg-muted/50 border border-border/50 rounded focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid={`${testIdPrefix}-input`}
        />
      </div>
      <div className="flex-1 overflow-auto px-1 py-1 min-h-0">
        {results.length === 0 && (
          <p className="text-xs text-muted-foreground italic px-2 py-1">No strings added</p>
        )}
        {results.map(({ str, result }, idx) => {
          const status = getStatus(result);
          const style = STATUS_STYLES[status];
          const Icon = style.icon;
          const displayStr = str || '\u03B5';
          const tapeOutput = isTM && typeof result === 'object' ? result.tape : null;
          const isDragging = dragIdx === idx;
          const isOver = overIdx === idx && dragIdx !== idx;
          return (
            <div
              key={str}
              draggable={!isReadOnly}
              onDragStart={(e) => handleDragStart(e, idx)}
              onDragOver={(e) => handleDragOver(e, idx)}
              onDrop={(e) => handleDrop(e, idx)}
              onDragEnd={handleDragEnd}
              className={`flex items-center gap-1 px-1 py-0.5 rounded group mb-0.5 transition-all ${style.bg} ${isDragging ? 'opacity-40' : ''} ${isOver ? 'ring-1 ring-ring' : ''}`}
              style={{ cursor: 'default' }}
              data-testid={`${testIdPrefix}-string-${str}`}
            >
              <GripVertical className={`w-3 h-3 shrink-0 text-muted-foreground/40 ${isReadOnly ? 'opacity-30' : 'cursor-grab active:cursor-grabbing'}`} />
              <Tooltip>
                <TooltipTrigger asChild>
                  <Icon className={`w-3.5 h-3.5 shrink-0 ${style.color}`} />
                </TooltipTrigger>
                <TooltipContent side="left">{style.label}</TooltipContent>
              </Tooltip>
              <span className={`text-sm font-mono min-w-0 truncate ${style.color} ${tapeOutput != null ? '' : 'flex-1'}`}>
                {displayStr}
              </span>
              {tapeOutput != null && (
                <span className="text-[10px] font-mono text-muted-foreground flex-1 min-w-0 truncate" data-testid={`${testIdPrefix}-tape-${str}`}>
                  {'\u2192'} {tapeOutput || '\u03B5'}
                </span>
              )}
              <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      className="p-0.5 hover:bg-muted rounded transition-colors"
                      onClick={() => onOpenSimulator(str)}
                      data-testid={`${testIdPrefix}-sim-${str}`}
                    >
                      <Play className="w-3 h-3 text-muted-foreground" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>Open in simulator</TooltipContent>
                </Tooltip>
                <button
                  className="p-0.5 hover:bg-muted rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                  onClick={() => onRemove(type, str)}
                  disabled={isReadOnly}
                  data-testid={`${testIdPrefix}-remove-${str}`}
                >
                  <Trash2 className="w-3 h-3 text-muted-foreground" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


const LEGEND_ITEMS = [
  { key: 'tp', label: 'True Positive', desc: 'should accept, accepts' },
  { key: 'fp', label: 'False Positive', desc: 'should reject, accepts' },
  { key: 'tn', label: 'True Negative', desc: 'should reject, rejects' },
  { key: 'fn', label: 'False Negative', desc: 'should accept, rejects' },
];

function Legend() {
  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const [compact, setCompact] = useState(true);

  useEffect(() => {
    const parent = containerRef.current?.parentElement;
    const measurer = measureRef.current;
    if (!parent || !measurer) return;
    const ro = new ResizeObserver(() => {
      const needed = measurer.scrollHeight;
      const available = parent.clientHeight - 10; // 10px breathing room
      setCompact(available < needed);
    });
    ro.observe(parent);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={containerRef} className="w-56 shrink-0 ml-2 mr-1 my-1 px-3 py-2 rounded-md bg-muted/40 border border-border/40 overflow-auto" data-testid="testing-legend">
      {/* Hidden measurer: always renders expanded layout to get real height */}
      <div ref={measureRef} className="absolute invisible pointer-events-none w-56 px-3 py-2" aria-hidden="true">
        <h4 className="text-[10px] font-semibold uppercase tracking-wider mb-2">Legend</h4>
        <div className="space-y-1">
          {LEGEND_ITEMS.map(({ key, label, desc }) => (
            <div key={key} className="flex items-center gap-1.5 px-2" style={{ paddingTop: 2, paddingBottom: 10 }}>
              <span className="w-3 h-3 shrink-0" />
              <div><span className="text-[11px]">{label}</span><p className="text-[10px] mt-0.5">{desc}</p></div>
            </div>
          ))}
        </div>
      </div>

      <h4 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Legend</h4>
      <div className="space-y-1">
        {LEGEND_ITEMS.map(({ key, label, desc }) => {
          const style = STATUS_STYLES[key];
          const Icon = style.icon;
          const inner = (
            <div key={key} className={`flex items-center gap-1.5 px-2 rounded ${style.bg}`} style={{ paddingTop: 2, paddingBottom: compact ? 2 : 10 }}>
              <Icon className={`w-3 h-3 shrink-0 ${style.color}`} />
              <div className="min-w-0">
                <span className={`text-[11px] leading-none font-medium ${style.color}`}>{label}</span>
                {!compact && <p className="text-[10px] text-muted-foreground leading-none mt-0.5">{desc}</p>}
              </div>
            </div>
          );
          if (compact) {
            return (
              <Tooltip key={key}>
                <TooltipTrigger asChild>{inner}</TooltipTrigger>
                <TooltipContent side="left" className="text-xs">{desc}</TooltipContent>
              </Tooltip>
            );
          }
          return inner;
        })}
      </div>
    </div>
  );
}
